import { useGridCellEditor } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTheme } from 'styled-components';
import { useCallbackRef } from '../../hooks/useCallbackRef';
import { EMPTY_OBJECT } from '../../utils';
import { IconButton } from '../Button';
import { Box } from '../Core';
import { AutocompleteDropdown, AutocompleteResult } from '../Form/AutocompleteDropdown';
import { useDropdownPopper } from '../Form/Dropdown';
import { Input } from '../Form/Input';
import { getDropdownItemHeight, useSearchSelect } from '../Form/SearchSelect';
import { FormControlSizes } from '../Form/types';
import { IconName } from '../Icons';
import type { AgGridSearchSelectDropdownProps } from './types';

export const AgGridSearchSelectDropdown = function AgGridSearchSelectDropdown<T>(
  props: AgGridSearchSelectDropdownProps<T>
) {
  const resolvedProps = useMemo(() => {
    if (props.useSearchSelectParams != null || props.colDef.cellEditorParams == null) {
      return props;
    }

    // AG Grid 35's reactive popup editor path can run cellEditorParams without passing the result
    // through to the React editor props, so recover the missing params from the column definition.
    const cellEditorParams =
      typeof props.colDef.cellEditorParams === 'function'
        ? props.colDef.cellEditorParams(props)
        : props.colDef.cellEditorParams;

    return cellEditorParams == null ? props : { ...props, ...cellEditorParams };
  }, [props]);

  const {
    value,
    onValueChange,
    useSearchSelectParams,
    useDropdownParams,
    maxHeight,
    showClear,
    showDropdownSearch,
    searchPlaceholder,
    portalize,
    size,
    ...cellEditorParams
  } = resolvedProps;
  const theme = useTheme();
  const resolvedShowDropdownSearch = showDropdownSearch ?? true;
  const resolvedPortalize = portalize ?? false;
  const resolvedSize = size ?? FormControlSizes.Small;

  const handleChange = useCallbackRef((newValue: T | undefined) => {
    onValueChange(newValue);
    cellEditorParams.api.stopEditing();
  });

  const dropdownRef = useRef<HTMLElement>(cellEditorParams.eGridCell);
  const inputRef = useRef<HTMLInputElement>(null);

  // hardcoded for now but can be parameterized in the future
  const dropdownSize = FormControlSizes.Small;

  const searchSelect = useSearchSelect({
    ...useSearchSelectParams,
    itemSize: getDropdownItemHeight(theme.baseSize, dropdownSize),
    inputRef,
    onChange: handleChange,
  });
  const { openMenu, isOpen, getInputProps, reset } = searchSelect;

  const dropdown = useDropdownPopper({
    ...useDropdownParams,
    isOpen,
    referenceElement: dropdownRef.current,
    // There are two cases of clicking outside: 1) you click on another editable cell in the blotter, or 2) you just click outside of the blotter
    // In case 1 -- this below will not be called as ag grid will unmount this component. For 2), we call stopEditing since we are indeed stopping editing by clicking outside.
    onClickOutside: () => {
      cellEditorParams.api.stopEditing();
    },
  });

  const handleClearClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      reset();
      handleChange(undefined);
      e.preventDefault();
    },
    [reset, handleChange]
  );

  useGridCellEditor({
    afterGuiAttached() {
      openMenu();
      inputRef.current?.focus();
    },
    focusIn() {
      inputRef.current?.focus();
    },
  } as Parameters<typeof useGridCellEditor>[0]);

  useEffect(() => {
    openMenu();
    inputRef.current?.focus();
  }, [openMenu]);

  return (
    <Box>
      <AutocompleteDropdown
        {...searchSelect}
        {...dropdown}
        portalize={resolvedPortalize}
        maxHeight={maxHeight}
        renderResult={AutocompleteResult}
        size={dropdownSize}
        childrenAboveResults={
          <Input
            style={resolvedShowDropdownSearch ? EMPTY_OBJECT : { display: 'none' }}
            placeholder={searchPlaceholder}
            size={resolvedSize}
            {...getInputProps({ ref: inputRef })}
            suffix={
              showClear &&
              value != null && (
                <IconButton
                  icon={IconName.Close}
                  size={(FormControlSizes.Default - 0.5) as FormControlSizes}
                  onClick={handleClearClick}
                  round
                  ghost
                  tabIndex={-1}
                  onFocus={e => e.stopPropagation()}
                  data-testid="dropdown-clear"
                />
              )
            }
          />
        }
      />
    </Box>
  );
};
